import { useEffect, useState, useMemo } from 'react';
import { BoxesIcon, SearchIcon, LayoutListIcon, LayoutGridIcon } from 'lucide-react';
import { ResourceTable, ResourceRow } from './ResourceTable';
import { ResourceDetailDrawer } from './ResourceDetailDrawer';

type ResourceStatus = 'started' | 'stopped' | 'starting' | 'stopping' | 'missing';
type FilterTab = 'all' | 'started' | 'stopped';

const STATUS_CONFIG: Record<ResourceStatus, { label: string; color: string; dot: string }> = {
  started:  { label: 'Started',  color: '#00ff66', dot: '#00ff66' },
  stopped:  { label: 'Stopped',  color: '#4a6680', dot: '#334155' },
  starting: { label: 'Starting', color: '#fbbf24', dot: '#fbbf24' },
  stopping: { label: 'Stopping', color: '#f87171', dot: '#f87171' },
  missing:  { label: 'Missing',  color: '#ef4444', dot: '#ef4444' },
};

function tickColor(ms: number | undefined): string {
  if (!ms) return '#4a6680';
  if (ms < 5)  return '#00ff66';
  if (ms < 15) return '#fbbf24';
  return '#f87171';
}

export default function ResourcesPage() {
  const [resources, setResources]       = useState<ResourceRow[]>([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filter, setFilter]             = useState<FilterTab>('all');
  const [view, setView]                 = useState<'table' | 'grid'>('table');
  const [actionTarget, setActionTarget] = useState<string | null>(null);
  const [selected, setSelected]         = useState<ResourceRow | null>(null);
  const [drawerOpen, setDrawerOpen]     = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await fetch('/resources');
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data?.resources)) setResources(data.resources);
      }
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAction = async (name: string, action: 'start' | 'stop' | 'restart') => {
    setActionTarget(`${name}:${action}`);
    try {
      await fetch(`/resources/${encodeURIComponent(name)}/${action}`, { method: 'POST' });
    } catch { /* noop */ }
    await load();
    setActionTarget(null);
  };

  const handleSelect = (r: ResourceRow) => {
    setSelected(r);
    setDrawerOpen(true);
  };

  const filtered = useMemo(() => {
    let list = resources;
    if (filter === 'started') list = list.filter(r => r.status === 'started');
    else if (filter === 'stopped') list = list.filter(r => r.status !== 'started');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.name.toLowerCase().includes(q) || r.category?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [resources, filter, search]);

  const running = resources.filter(r => r.status === 'started');
  const stopped = resources.filter(r => r.status !== 'started');

  return (
    <div className="p-4 md:p-6 flex flex-col gap-5">
      {/* Header */}
      <div>
        <div className="text-[9px] font-mono tracking-[0.2em] uppercase mb-1" style={{ color: 'rgba(0,255,209,0.35)' }}>Server</div>
        <h1 className="text-2xl font-black tracking-tight" style={{ color: 'var(--sc-text)', letterSpacing: '-0.04em' }}>Resources</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--sc-muted)' }}>
          {loading ? 'Loading…' : `${running.length} running · ${stopped.length} stopped · ${resources.length} total`}
        </p>
      </div>

      {/* KPI pills */}
      <div className="flex gap-3 flex-wrap">
        {[
          { label: 'Running', value: String(running.length), color: '#00ff66' },
          { label: 'Stopped', value: String(stopped.length), color: '#4a6680' },
          { label: 'Total',   value: String(resources.length), color: 'var(--sc-cyan)' },
        ].map(s => (
          <div key={s.label} className="sc-glass flex items-center gap-2 px-4 py-2.5 rounded-xl">
            <BoxesIcon className="h-3.5 w-3.5 shrink-0" style={{ color: s.color }} />
            <div>
              <div className="text-[9px] font-mono uppercase tracking-wider" style={{ color: 'var(--sc-muted)' }}>{s.label}</div>
              <div className="text-sm font-bold font-mono" style={{ color: s.color }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: 'var(--sc-muted)' }} />
          <input
            type="text"
            placeholder="Search resources…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm font-mono outline-none"
            style={{
              background: 'var(--sc-bg-elevated)',
              borderColor: 'var(--sc-border)',
              color: 'var(--sc-text)',
              caretColor: 'var(--sc-cyan)',
            }}
          />
        </div>

        {/* Filter tabs */}
        <div
          className="flex items-center gap-1 p-1 rounded-lg"
          style={{ background: 'var(--sc-bg-elevated)', border: '1px solid var(--sc-border)' }}
        >
          {(['all', 'started', 'stopped'] as FilterTab[]).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all"
              style={{
                background: filter === f ? 'var(--sc-bg-overlay)' : 'transparent',
                color: filter === f ? 'var(--sc-cyan)' : 'var(--sc-muted)',
                border: filter === f ? '1px solid var(--sc-border-mid)' : '1px solid transparent',
              }}
            >
              {f}
            </button>
          ))}
        </div>

        {/* View toggle */}
        <div
          className="flex items-center gap-1 p-1 rounded-lg ml-auto"
          style={{ background: 'var(--sc-bg-elevated)', border: '1px solid var(--sc-border)' }}
        >
          {([['table', LayoutListIcon], ['grid', LayoutGridIcon]] as const).map(([v, Icon]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className="p-1.5 rounded-md transition-all"
              style={{
                background: view === v ? 'var(--sc-bg-overlay)' : 'transparent',
                color: view === v ? 'var(--sc-cyan)' : 'var(--sc-muted)',
              }}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      {search && (
        <div className="text-[10px] font-mono" style={{ color: 'var(--sc-muted)' }}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''} for &ldquo;{search}&rdquo;
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-sm font-mono text-center py-12" style={{ color: 'var(--sc-muted)' }}>
          <i className="fa-solid fa-spinner animate-spin mr-2" style={{ color: 'var(--sc-cyan)' }} />
          Loading resources…
        </div>
      ) : view === 'table' ? (
        <ResourceTable
          rows={filtered}
          onSelect={handleSelect}
          onAction={handleAction}
          actionTarget={actionTarget}
        />
      ) : (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(r => {
            const cfg = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.stopped;
            const isPending = actionTarget?.startsWith(`${r.name}:`);
            return (
              <div
                key={r.name}
                onClick={() => handleSelect(r)}
                className="sc-glass rounded-xl p-4 flex flex-col gap-3 cursor-pointer hover:border-white/[0.1] transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{
                      background: cfg.dot,
                      boxShadow: r.status === 'started' ? `0 0 6px ${cfg.dot}` : 'none',
                    }}
                  />
                  <span className="text-[12px] font-mono font-semibold truncate flex-1" style={{ color: 'var(--sc-text)' }}>
                    {r.name}
                  </span>
                  {r.tick !== undefined && (
                    <span className="text-[9px] font-mono" style={{ color: tickColor(r.tick) }}>
                      {r.tick.toFixed(1)}ms
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="text-[9px] font-mono px-2 py-0.5 rounded border"
                    style={{ color: cfg.color, background: cfg.color + '12', borderColor: cfg.color + '28' }}
                  >
                    {cfg.label}
                  </span>
                  {r.category && (
                    <span className="text-[9px] font-mono truncate" style={{ color: 'var(--sc-muted)' }}>
                      {r.category}
                    </span>
                  )}
                </div>
                <div
                  className="flex gap-1.5 pt-1 border-t"
                  style={{ borderColor: 'var(--sc-border)' }}
                  onClick={e => e.stopPropagation()}
                >
                  {r.status === 'stopped' || r.status === 'missing' ? (
                    <button
                      disabled={!!isPending}
                      onClick={() => handleAction(r.name, 'start')}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-bold transition-all disabled:opacity-50"
                      style={{ background: 'rgba(0,255,102,0.1)', color: '#00ff66' }}
                    >
                      Start
                    </button>
                  ) : (
                    <>
                      <button
                        disabled={!!isPending}
                        onClick={() => handleAction(r.name, 'restart')}
                        className="flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all disabled:opacity-50"
                        style={{ background: 'rgba(0,255,209,0.07)', color: 'var(--sc-cyan)' }}
                      >
                        Restart
                      </button>
                      <button
                        disabled={!!isPending}
                        onClick={() => handleAction(r.name, 'stop')}
                        className="flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all disabled:opacity-50"
                        style={{ background: 'rgba(239,68,68,0.07)', color: '#f87171' }}
                      >
                        Stop
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Drawer */}
      <ResourceDetailDrawer
        resource={selected}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onAction={handleAction}
        actionTarget={actionTarget}
      />
    </div>
  );
}
